import React, { useState, useMemo } from 'react';
import { icons as LucideIcons, X } from 'lucide-react';

// lucide-react icons are forwardRef components (typeof === 'object'), not plain functions
type LucideIcon = React.ComponentType<{ size?: number; color?: string; strokeWidth?: number; className?: string }>;

function isIcon(val: unknown): val is LucideIcon {
  if (typeof val === 'function') return true;
  if (val && typeof val === 'object') {
    const t = (val as Record<string, unknown>).$$typeof;
    return t === Symbol.for('react.forward_ref') || t === Symbol.for('react.memo');
  }
  return false;
}

function getIcon(name: string): LucideIcon | undefined {
  const icon = (LucideIcons as Record<string, unknown>)[name];
  return isIcon(icon) ? icon : undefined;
}

// Curated icons with Norwegian labels and legacy emoji identifiers
const CURATED_DEFS: Array<{ name: string; label: string; emoji?: string }> = [
  { name: 'CircleSlash',          label: 'Standard / ingen',                  emoji: '' },
  // ── Håndverktøy ──────────────────────────────────────────
  { name: 'Wrench',               label: 'Skrunøkkel',                        emoji: '🔧' },
  { name: 'Hammer',               label: 'Hammer',                            emoji: '🔨' },
  { name: 'Drill',                label: 'Bor / drill',                       emoji: '🪛' },
  { name: 'Pickaxe',              label: 'Hakke / pickaxe',                   emoji: '🪨' },
  { name: 'Axe',                  label: 'Øks',                               emoji: '🪓' },
  { name: 'PocketKnife',          label: 'Lommekniv / brekkjern',             emoji: '🔪' },
  { name: 'Scissors',             label: 'Saks / kutter',                     emoji: '✂️' },
  { name: 'Ruler',                label: 'Lineal / målebånd',                 emoji: '📏' },
  { name: 'DraftingCompass',      label: 'Tegnesirkel / vinkelmåler',         emoji: '📐' },
  { name: 'PencilRuler',          label: 'Oppmåling / tegning',               emoji: '📝' },
  { name: 'Shovel',               label: 'Spade / skuffe',                    emoji: '🪠' },
  { name: 'Layers',               label: 'Stige',                             emoji: '🪜' },
  { name: 'GripVertical',         label: 'Tvinge / klemme',                   emoji: '🗜️' },
  { name: 'Crosshair',            label: 'Vater / lasernivå',                 emoji: '🎯' },
  { name: 'Scale',                label: 'Balansevater / vekt',               emoji: '⚖️' },
  { name: 'Nut',                  label: 'Mutter / skrue / bolt',             emoji: '🔩' },
  { name: 'Waves',                label: 'Sag / sagblad',                     emoji: '🪚' },
  { name: 'Weight',               label: 'Vektlodd',                          emoji: '🏋️' },
  { name: 'Target',               label: 'Presisjon / sikte',                 emoji: '🎪' },
  // ── Maskin & konstruksjon ────────────────────────────────
  { name: 'Cog',                  label: 'Motor / maskin',                    emoji: '⚙️' },
  { name: 'Settings',             label: 'Generelt verktøy',                  emoji: '🛠️' },
  { name: 'Construction',         label: 'Anleggsarbeid',                     emoji: '🚧' },
  { name: 'HardHat',              label: 'Vernehjelm',                        emoji: '⛑️' },
  { name: 'BrickWall',            label: 'Mur / betong / murverk',            emoji: '🧱' },
  { name: 'Magnet',               label: 'Magnet',                            emoji: '🧲' },
  { name: 'Gauge',                label: 'Manometer / trykkmåler',            emoji: '📊' },
  { name: 'CircleGauge',          label: 'Rund gauge / instrumenter',         emoji: '🔵' },
  { name: 'Siren',                label: 'Alarm / varsler',                   emoji: '🚨' },
  { name: 'AlarmSmoke',           label: 'Røykvarsler / brannalarm',          emoji: '🔔' },
  // ── Elektrisk & VVS ─────────────────────────────────────
  { name: 'Plug',                 label: 'Stikkontakt',                       emoji: '🔌' },
  { name: 'Plug2',                label: 'Skjøteledning',                     emoji: '🪫' },
  { name: 'PlugZap',              label: 'Jordfeil / sikring',                emoji: '🌩️' },
  { name: 'Unplug',               label: 'Frakobling / nødstans',             emoji: '🧯' },
  { name: 'HousePlug',            label: 'El-installasjon / jordfeil',        emoji: '🔅' },
  { name: 'HouseWifi',            label: 'Smarthus / automatikk',             emoji: '🌐' },
  { name: 'Zap',                  label: 'Strøm / spenning',                  emoji: '⚡' },
  { name: 'Bolt',                 label: 'Overspenning / lynvern',            emoji: '⚡️' },
  { name: 'Battery',              label: 'Batteri',                           emoji: '🔋' },
  { name: 'BatteryCharging',      label: 'Lading / ladesystem',               emoji: '🔌🔋' },
  { name: 'Lightbulb',            label: 'Lyspære / belysning',               emoji: '💡' },
  { name: 'Flashlight',           label: 'Lommelykt',                         emoji: '🔦' },
  { name: 'Spotlight',            label: 'Spotlight / bygglampe',             emoji: '🔆' },
  { name: 'Cable',                label: 'Kabel / ledning',                   emoji: '🔗' },
  { name: 'Heater',               label: 'Varmeovn / panelovn',               emoji: '🌡️' },
  { name: 'AirVent',              label: 'Ventilasjon / varmepumpe',          emoji: '💨' },
  { name: 'ShowerHead',           label: 'Rørlegger / VVS',                   emoji: '🚿' },
  { name: 'Thermometer',          label: 'Termometer',                        emoji: '🌡' },
  { name: 'ThermometerSnowflake', label: 'Kjøleromstermometer / frysing',     emoji: '🧊' },
  { name: 'ThermometerSun',       label: 'Varmetermometer / sol',             emoji: '🌤️' },
  { name: 'Droplets',             label: 'Vann / lekkasje / rør',             emoji: '💧' },
  { name: 'Droplet',              label: 'Dråpe / drypp',                     emoji: '🫧' },
  // ── Maling, lim & overflate ─────────────────────────────
  { name: 'Palette',              label: 'Maling / farger',                   emoji: '🎨' },
  { name: 'SprayCan',             label: 'Sprayboks / aerosol / lakk',        emoji: '🧴' },
  { name: 'PaintBucket',          label: 'Malingsbøtte / spartelmasse',       emoji: '🪣' },
  { name: 'PaintRoller',          label: 'Malerulle / veggmaling',            emoji: '🖼️' },
  { name: 'Paintbrush',           label: 'Pensel / malepensel',               emoji: '🖌️' },
  { name: 'Paintbrush2',          label: 'Bred pensel / kalkun',              emoji: '🖍️' },
  { name: 'PaintbrushVertical',   label: 'Kantpensel / strykepinne',          emoji: '🪆' },
  { name: 'Brush',                label: 'Kost / børste',                     emoji: '🧹' },
  { name: 'BrushCleaning',        label: 'Skrubbebørste / rengjøring',        emoji: '🧽' },
  { name: 'Highlighter',          label: 'Marker / tusj',                     emoji: '🖊️' },
  { name: 'Pipette',              label: 'Lim / pipette / herdetid',          emoji: '💉' },
  { name: 'Eraser',               label: 'Sliping / pussing',                 emoji: '🧼' },
  { name: 'FlaskConical',         label: 'Kjemikalier / lim / løsemidler',    emoji: '🧪' },
  { name: 'FlaskRound',           label: 'Flytende maling / lakk',            emoji: '⚗️' },
  { name: 'TestTube',             label: 'Fugeprøve / fargtest',              emoji: '🔬' },
  // ── Hus & interiør ───────────────────────────────────────
  { name: 'Home',                 label: 'Hjem / innendørs',                  emoji: '🏠' },
  { name: 'House',                label: 'Hus / bolig',                       emoji: '🏡' },
  { name: 'Key',                  label: 'Nøkkel',                            emoji: '🔑' },
  { name: 'Lock',                 label: 'Lås / sikkerhet',                   emoji: '🔒' },
  { name: 'DoorOpen',             label: 'Dør / port',                        emoji: '🚪' },
  { name: 'DoorClosed',           label: 'Lukket rom / tilgangskontroll',     emoji: '🚫' },
  { name: 'Sofa',                 label: 'Møbler / interiør',                 emoji: '🛋️' },
  { name: 'Fan',                  label: 'Vifte / luftsirkulasjon',           emoji: '🌀' },
  { name: 'WashingMachine',       label: 'Vaskemaskin / hvitevarer',          emoji: '🧺' },
  { name: 'Tv',                   label: 'TV / AV-utstyr',                    emoji: '📺' },
  { name: 'Fence',                label: 'Gjerde / innhegning',               emoji: '🏘️' },
  { name: 'Warehouse',            label: 'Lager / garasje / bod',             emoji: '🏭' },
  // ── Hage & utendørs ─────────────────────────────────────
  { name: 'Leaf',                 label: 'Hage / planter',                    emoji: '🌿' },
  { name: 'LeafyGreen',           label: 'Grønnsaker / kjøkkenhage',          emoji: '🥬' },
  { name: 'Sprout',               label: 'Frøplanting / spiring',             emoji: '🌱' },
  { name: 'Flower',               label: 'Blomster / potteplanter',           emoji: '🌸' },
  { name: 'TreePine',             label: 'Trær / skog / treskjæring',         emoji: '🌲' },
  { name: 'Wheat',                label: 'Grønnsaker / åkerbruk',             emoji: '🌾' },
  { name: 'Birdhouse',            label: 'Fuglekasse / hagepynt',             emoji: '🐦' },
  { name: 'Recycle',              label: 'Kompostering / gjenbruk',           emoji: '♻️' },
  { name: 'Flame',                label: 'Bål / sveising / åpen ild',         emoji: '🔥' },
  { name: 'FlameKindling',        label: 'Kaminved / brensel',                emoji: '🪵' },
  { name: 'Fuel',                 label: 'Drivstoff / bensin / diesel',       emoji: '⛽' },
  { name: 'Umbrella',             label: 'Utendørs / hagemøbler',             emoji: '☂️' },
  { name: 'Sun',                  label: 'Sol / sommer / hagesesong',         emoji: '☀️' },
  { name: 'Wind',                 label: 'Vindmåler / lufting',               emoji: '🌬️' },
  { name: 'Snowflake',            label: 'Vinter / snørydding / kjøling',     emoji: '❄️' },
  { name: 'Moon',                 label: 'Natt / sesonglagring',              emoji: '🌙' },
  { name: 'WavesLadder',          label: 'Basseng / svømmeutstyr',            emoji: '🏊' },
  { name: 'FishingHook',          label: 'Fiskeutstyr / kroker',              emoji: '🎣' },
  // ── Kjøretøy & tunge maskiner ───────────────────────────
  { name: 'Tractor',              label: 'Traktor / gårdsmaskin',             emoji: '🚜' },
  { name: 'Truck',                label: 'Lastebil / transport',              emoji: '🚚' },
  { name: 'Car',                  label: 'Bil / kjøretøy',                    emoji: '🚗' },
  { name: 'Bike',                 label: 'Sykkel / moped',                    emoji: '🚲' },
  { name: 'Anchor',               label: 'Båt / marine',                      emoji: '⚓' },
  { name: 'Forklift',             label: 'Truck / heiseutstyr',               emoji: '🏗️' },
  // ── Sport & fritid ───────────────────────────────────────
  { name: 'Dumbbell',             label: 'Trening / vekter',                  emoji: '🏋️‍♂️' },
  { name: 'Compass',              label: 'Friluft / orientering',             emoji: '🧭' },
  { name: 'Mountain',             label: 'Fjell / klatring',                  emoji: '⛰️' },
  { name: 'Camera',               label: 'Foto / video',                      emoji: '📷' },
  { name: 'Music',                label: 'Musikk / lydanlegg',                emoji: '🎵' },
  { name: 'Star',                 label: 'Favoritt / annet',                  emoji: '⭐' },
  // ── Teknologi & hvitevarer ───────────────────────────────
  { name: 'Cpu',                  label: 'Elektronikk / IT',                  emoji: '💻' },
  { name: 'Monitor',              label: 'Skjerm / datamaskin',               emoji: '🖥️' },
  { name: 'Printer',              label: 'Printer / skanner / kopimaskin',    emoji: '🖨️' },
  { name: 'Wifi',                 label: 'Nettverk / internett',              emoji: '📶' },
  { name: 'Server',               label: 'Server / NAS / lagring',            emoji: '🗄️' },
  { name: 'Smartphone',           label: 'Mobil / nettbrett',                 emoji: '📱' },
  { name: 'Microwave',            label: 'Mikrobølgeovn / hvitevarer',        emoji: '🎛️' },
  // ── Diverse ──────────────────────────────────────────────
  { name: 'ShoppingCart',         label: 'Diverse / innkjøp',                 emoji: '🛒' },
  { name: 'Package',              label: 'Pakke / emballasje',                emoji: '📦' },
  { name: 'Archive',              label: 'Arkiv / langtidslagring',           emoji: '🗃️' },
  { name: 'Tag',                  label: 'Merking / etikett',                 emoji: '🏷️' },
  { name: 'BookOpen',             label: 'Manualer / instruksjonsbøker',      emoji: '📖' },
  { name: 'Shirt',                label: 'Arbeidsklær / verneutstyr',         emoji: '👕' },
  { name: 'Coffee',               label: 'Pause / annet',                     emoji: '☕' },
];

// Build lookup maps from curated defs
const EMOJI_TO_NAME: Record<string, string> = {};
const NAME_TO_LABEL: Record<string, string> = {};
for (const { name, label, emoji } of CURATED_DEFS) {
  NAME_TO_LABEL[name] = label;
  if (emoji) EMOJI_TO_NAME[emoji] = name;
}

// Extra legacy emoji mappings
const EXTRA_EMOJI: Record<string, string> = {
  '🧰': 'Wrench', '🔩': 'Nut', '⛏️': 'Hammer', '⛏': 'Pickaxe',
  '🌱': 'Sprout', '🪴': 'Leaf', '🔬': 'TestTube',
  '🪤': 'Settings', '🚜': 'Tractor', '🪛': 'Drill',
  '🖍': 'Paintbrush2', '🎠': 'PaintRoller', '🪚': 'Waves',
};

// Build full icon list: curated first, then all remaining Lucide icons
const curatedNames = new Set(CURATED_DEFS.map(d => d.name));

type IconEntry = { name: string; Icon: LucideIcon; label: string; curated: boolean };

const ALL_ICONS: IconEntry[] = [
  ...CURATED_DEFS
    .map(({ name, label }) => {
      const Icon = getIcon(name);
      if (!Icon) return null;
      return { name, Icon, label, curated: true };
    })
    .filter((x): x is IconEntry => x !== null),
  ...Object.entries(LucideIcons)
    .filter(([name, comp]) =>
      isIcon(comp) &&
      /^[A-Z]/.test(name) &&
      !curatedNames.has(name)
    )
    .map(([name, comp]) => ({
      name,
      Icon: comp as LucideIcon,
      label: name.replace(/([A-Z])/g, ' $1').trim(),
      curated: false,
    })),
];

const CURATED_ICONS = ALL_ICONS.filter(o => o.curated);

// Kept for backward compatibility with any external references
export const CATEGORY_ICON_OPTIONS = CURATED_DEFS.map(({ name, label, emoji = '' }) => ({
  emoji,
  label,
  Icon: getIcon(name) ?? (getIcon('CircleSlash') as LucideIcon),
}));

const FallbackIcon: React.FC<{ size: number; color: string; strokeWidth: number }> = ({ size, color, strokeWidth }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} stroke={color} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);

export const CategoryIcon: React.FC<{ emoji: string | null | undefined; size?: number; color?: string; strokeWidth?: number }> = ({
  emoji, size = 18, color = 'currentColor', strokeWidth = 1.8,
}) => {
  const safe = emoji ?? '';

  // 1. Try as Lucide icon name (new format: "Wrench", "Hammer" etc.)
  if (safe) {
    const ByName = getIcon(safe);
    if (ByName) return <ByName size={size} color={color} strokeWidth={strokeWidth} />;
  }

  // 2. Legacy emoji → icon name mapping
  if (safe) {
    const iconName = EMOJI_TO_NAME[safe] ?? EXTRA_EMOJI[safe];
    if (iconName) {
      const ByEmoji = getIcon(iconName);
      if (ByEmoji) return <ByEmoji size={size} color={color} strokeWidth={strokeWidth} />;
    }
  }

  // 3. Safe inline fallback (CircleSlash-style)
  const Default = getIcon('CircleSlash');
  if (Default) return <Default size={size} color={color} strokeWidth={strokeWidth} />;
  return <FallbackIcon size={size} color={color} strokeWidth={strokeWidth} />;
};

export const CategoryIconPicker: React.FC<{ value: string; onChange: (name: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Find selected — handle both new icon-name format and legacy emoji
  const resolvedName = getIcon(value) ? value : (EMOJI_TO_NAME[value] ?? EXTRA_EMOJI[value] ?? null);
  const selected = resolvedName ? ALL_ICONS.find(o => o.name === resolvedName) : undefined;

  const { displayed, totalMatches } = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || q.length < 2) return { displayed: CURATED_ICONS, totalMatches: CURATED_ICONS.length };
    const matches = ALL_ICONS.filter(o =>
      o.label.toLowerCase().includes(q) || o.name.toLowerCase().includes(q)
    );
    return { displayed: matches.slice(0, 120), totalMatches: matches.length };
  }, [search]);

  const isSearching = search.trim().length >= 2;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          onClick={() => { setOpen(o => !o); if (open) setSearch(''); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '7px 13px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-strong)', background: 'var(--bg)',
            fontSize: 13, color: 'var(--text2)', cursor: 'pointer',
          }}
        >
          {selected
            ? <><selected.Icon size={16} color="var(--green)" strokeWidth={1.8} /><span style={{ color: 'var(--text)' }}>{selected.label}</span></>
            : <span>Velg ikon</span>}
          <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 2 }}>{open ? '▲' : '▼'}</span>
        </button>
        {selected && (
          <button
            type="button"
            title="Fjern ikon"
            onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, borderRadius: '50%',
              border: '1px solid var(--border-strong)', background: 'var(--bg)',
              color: 'var(--text3)', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 8 }}>
          <input
            type="text"
            placeholder={`Søk blant ${ALL_ICONS.length} ikoner...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setOpen(false)}
            autoFocus
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-strong)', background: 'var(--bg)',
              fontSize: 14, color: 'var(--text)', marginBottom: 6,
              boxSizing: 'border-box' as const, outline: 'none',
            }}
          />
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
            {isSearching
              ? `${totalMatches} treff${totalMatches > 120 ? ' – viser 120' : ''}`
              : `Viser ${CURATED_ICONS.length} anbefalte. Søk for å finne alle ${ALL_ICONS.length}.`}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {displayed.map(({ name, Icon, label }) => {
              const active = resolvedName === name || value === name;
              return (
                <button
                  key={name}
                  type="button"
                  title={label}
                  onClick={() => { onChange(name); setOpen(false); setSearch(''); }}
                  style={{
                    width: 40, height: 40, borderRadius: 'var(--radius-xs)',
                    border: `2px solid ${active ? 'var(--green)' : 'var(--border)'}`,
                    background: active ? 'var(--green-light)' : 'var(--bg)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <Icon size={18} color={active ? 'var(--green)' : 'var(--text2)'} strokeWidth={1.8} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
